import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import '../styles/SidebarMenu.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBars, 
  faTimes, 
  faHome, 
  faKeyboard, 
  faChevronRight,
  faBullseye,
  faGamepad,
  faNewspaper
} from '@fortawesome/free-solid-svg-icons';

const PURPLE = '#a259ff';

const SidebarContainer = styled.div`
  background: #1a1a1a;
  color: #fff;
  transition: transform 0.3s ease-in-out, visibility 0.3s ease-in-out;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const NavList = styled.ul`
  list-style: none;
  padding: 80px 20px 20px;
  margin: 0;
`;

const SidebarLink = styled(Link).attrs(props => ({ tabIndex: 0 }))`
  color: #fff;
  text-decoration: none;
  font-size: 14px;
  display: flex;
  align-items: center;
  padding: 10px;
  border-radius: 5px;
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
  margin-left: -10px;
  margin-right: -10px;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  /* --- CHANGE: Apply hover/focus only on devices that support it --- */
  @media (hover: hover) and (pointer: fine) {
    &:hover, &:focus {
      background: rgba(255, 255, 255, 0.1);
      border-left: 4px solid ${PURPLE};
      transform: translateX(5px);
    }
  }

  /* --- CHANGE: Apply active styles only on non-mobile devices --- */
  @media (min-width: 769px) {
    ${props => props.$active && `
      background: rgba(255,255,255,0.08);
      border-left: 4px solid ${PURPLE};
    `}
  }
`;

const SubMenuSidebarLink = styled(SidebarLink)`
  margin-left: -10px;
  margin-right: -10px;
  width: calc(100% + 20px);
  padding: 10px 10px 10px 20px;
  transform: translateX(0);
  transition: all 0.3s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  /* Apply hover/focus only on devices that support it */
  @media (hover: hover) and (pointer: fine) {
    &:hover, &:focus {
      transform: translateX(5px);
    }
  }
`;

const SubMenu = styled.ul`
  list-style: none;
  padding-left: 0;
  margin: 0;
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  padding: 10px;
  cursor: pointer;
  border-radius: 5px;
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
  margin-left: -10px;
  margin-right: -10px;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  /* --- CHANGE: Apply hover/focus only on devices that support it --- */
  @media (hover: hover) and (pointer: fine) {
    &:hover, &:focus {
      background: rgba(255, 255, 255, 0.1);
      border-left: 4px solid ${PURPLE};
      transform: translateX(5px);
    }
  }

  .dropdown-icon {
    transition: transform 0.3s ease;
    transform: ${props => props.$open ? 'rotate(90deg)' : 'rotate(0)'};
  }
`;

const Overlay = styled.div`
  background: rgba(0, 0, 0, 0.5);
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const IconWrapper = styled.span`
font-size: 14px;
  margin-right: 10px;
  width: 20px;
  text-align: center;
`;

export default function SidebarMenu() {
  const [open, setOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(false);
  const [openClickSubmenu, setOpenClickSubmenu] = useState(false);
  const [openGamesSubmenu, setOpenGamesSubmenu] = useState(false);
  const location = useLocation();
  const pathMatches = (base, { prefix } = {}) => {
    const current = location.pathname.replace(/\/+$/, '') || '/';
    const target = base.replace(/\/+$/, '') || '/';
    if (prefix) return current === target || current.startsWith(`${target}/`);
    return current === target;
  };

  const handleClose = () => {
    setOpen(false);
    setOpenSubmenu(false);
    setOpenClickSubmenu(false);
    setOpenGamesSubmenu(false);
  };

  const toggleSubmenu = () => {
    setOpenSubmenu(!openSubmenu);
  };

  const toggleClickSubmenu = () => {
    setOpenClickSubmenu(!openClickSubmenu);
  };

  const toggleGamesSubmenu = () => {
    setOpenGamesSubmenu(!openGamesSubmenu);
  };

  return (
    <>
      <div className="sidebar-chrome">
        {!open && (
          <MenuButton className="sidebar-menu-button" type="button" aria-label="Open menu" onClick={() => setOpen(true)}>
            <FontAwesomeIcon icon={faBars} />
          </MenuButton>
        )}
      </div>
      {open && (
        <>
      <Overlay
        className="sidebar-overlay is-open"
        onClick={handleClose}
      />
      <SidebarContainer
        className="sidebar-drawer is-open"
      >
        <CloseButton className="sidebar-close-button" type="button" aria-label="Close menu" onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
        <NavList className="sidebar-nav">
          <li>
            <SidebarLink to="/" onClick={handleClose} $active={pathMatches("/")}>
              <IconWrapper>
                <FontAwesomeIcon icon={faHome} />
              </IconWrapper>
              Home
            </SidebarLink>
          </li>
          <li>
            <SidebarLink to="/blog/" onClick={handleClose} $active={pathMatches("/blog", { prefix: true })}>
              <IconWrapper>
                <FontAwesomeIcon icon={faNewspaper} />
              </IconWrapper>
              Blog
            </SidebarLink>
          </li>
          <li>
            <MenuItem $open={openSubmenu} onClick={toggleSubmenu}>
              <div>
                <IconWrapper>
                  <FontAwesomeIcon icon={faKeyboard} />
                </IconWrapper>
                Typing Tests
              </div>
              <FontAwesomeIcon icon={faChevronRight} className="dropdown-icon" />
            </MenuItem>
            {openSubmenu && (
            <SubMenu className="sidebar-submenu is-open">
              <li>
                <SubMenuSidebarLink to="/15s-cpm-typing-test/" onClick={handleClose} $active={pathMatches("/15s-cpm-typing-test")}>
                  15s Test
                </SubMenuSidebarLink>
              </li>
              <li>
                <SubMenuSidebarLink to="/30s-cpm-typing-test/" onClick={handleClose} $active={pathMatches("/30s-cpm-typing-test")}>
                  30s Test
                </SubMenuSidebarLink>
              </li>
              <li>
                <SubMenuSidebarLink to="/60s-cpm-typing-test/" onClick={handleClose} $active={pathMatches("/60s-cpm-typing-test")}>
                  1min Test
                </SubMenuSidebarLink>
              </li>
              <li>
                <SubMenuSidebarLink to="/3min-cpm-typing-test/" onClick={handleClose} $active={pathMatches("/3min-cpm-typing-test")}>
                  3min Test
                </SubMenuSidebarLink>
              </li>
              <li>
                <SubMenuSidebarLink to="/5min-cpm-typing-test/" onClick={handleClose} $active={pathMatches("/5min-cpm-typing-test")}>
                  5min Test
                </SubMenuSidebarLink>
              </li>
            </SubMenu>
            )}
          </li>
          <li>
            <MenuItem $open={openClickSubmenu} onClick={toggleClickSubmenu}>
              <div>
                <IconWrapper>
                  <FontAwesomeIcon icon={faBullseye} />
                </IconWrapper>
                Click Tests
              </div>
              <FontAwesomeIcon icon={faChevronRight} className="dropdown-icon" />
            </MenuItem>
            {openClickSubmenu && (
            <SubMenu className="sidebar-submenu is-open">
              <li>
                <SubMenuSidebarLink to="/1s-cps-click-test/" onClick={handleClose} $active={pathMatches("/1s-cps-click-test")}>
                  1 Second CPS Burst
                </SubMenuSidebarLink>
              </li>
              <li>
                <SubMenuSidebarLink to="/5s-cps-click-test/" onClick={handleClose} $active={pathMatches("/5s-cps-click-test")}>
                  5 Second Click Test
                </SubMenuSidebarLink>
              </li>
              <li>
                <SubMenuSidebarLink to="/15s-cps-click-test/" onClick={handleClose} $active={pathMatches("/15s-cps-click-test")}>
                  15 Second Click Test
                </SubMenuSidebarLink>
              </li>
              <li>
                <SubMenuSidebarLink to="/30s-cps-click-test/" onClick={handleClose} $active={pathMatches("/30s-cps-click-test")}>
                  30 Second Click Test
                </SubMenuSidebarLink>
              </li>
              <li>
                <SubMenuSidebarLink to="/60s-cps-click-test/" onClick={handleClose} $active={pathMatches("/60s-cps-click-test")}>
                  1 Minute Click Test
                </SubMenuSidebarLink>
              </li>
            </SubMenu>
            )}
          </li>
          <li>
            <MenuItem $open={openGamesSubmenu} onClick={toggleGamesSubmenu}>
              <div>
                <IconWrapper>
                  <FontAwesomeIcon icon={faGamepad} />
                </IconWrapper>
                Games
              </div>
              <FontAwesomeIcon icon={faChevronRight} className="dropdown-icon" />
            </MenuItem>
            {openGamesSubmenu && (
            <SubMenu className="sidebar-submenu is-open">
              <li>
                <SubMenuSidebarLink to="/higher-or-lower/" onClick={handleClose} $active={pathMatches("/higher-or-lower")}>
                  Higher or Lower
                </SubMenuSidebarLink>
              </li>
              <li>
                <SubMenuSidebarLink to="/color-memory/" onClick={handleClose} $active={pathMatches("/color-memory")}>
                  Color Memory
                </SubMenuSidebarLink>
              </li>
              <li>
                <SubMenuSidebarLink to="/stop-the-wheel/" onClick={handleClose} $active={pathMatches("/stop-the-wheel")}>
                  Stop the Wheel
                </SubMenuSidebarLink>
              </li>
              <li>
                <SubMenuSidebarLink to="/reaction-test/" onClick={handleClose} $active={pathMatches("/reaction-test")}>
                  F1 Reaction Test
                </SubMenuSidebarLink>
              </li>
              <li>
                <SubMenuSidebarLink to="/guess-the-country/" onClick={handleClose} $active={pathMatches("/guess-the-country")}>
                  Guess the Country
                </SubMenuSidebarLink>
              </li>
              <li>
                <SubMenuSidebarLink to="/which-country-is-gayer/" onClick={handleClose} $active={pathMatches("/which-country-is-gayer")}>
                  Which Country Is Gayer?
                </SubMenuSidebarLink>
              </li>
            </SubMenu>
            )}
          </li>
        </NavList>
      </SidebarContainer>
        </>
      )}
    </>
  );
}